import _ from 'lodash';

import { Proctor } from '../types/messaging-types';
import { SimpleMessagingServiceClient } from '../lib/simple-messaging-client';

import { PageConfigurationData } from '../server/models/PageConfigurationData';
import { PageInitializationData } from '../server/models/PageInitializationData';

export interface BrowserStore {
    // tslint:disable-next-line: no-any
    pageConfigData: { [index: string]: any };
    // tslint:disable-next-line: no-any
    pageInitData: { [index: string]: any };

    instanceRegistry: {
        msgsClient?: SimpleMessagingServiceClient;
        proctor?: Proctor;
    };
}

export const MSGGQL_QUERIES_FOR_EVENT_LIST = [
    'GetConversationEvents',
    'GetConversationById',
    'UpdateConversationReadCursor',
];

// module level data store for App Data; server-side provided data that is static,
// but is necessary for behavior in the Client App.
let browserStore: Readonly<BrowserStore> | undefined;
export const initBrowserStore = (
    pageConfigData: PageConfigurationData,
    pageInitData: PageInitializationData,
    instanceRegistry: {
        msgsClient?: SimpleMessagingServiceClient;
        proctor?: Proctor;
    }
): Readonly<BrowserStore> => {
    if (!browserStore) {
        if (!instanceRegistry.msgsClient) {
            const clientOptions = _.get(pageInitData, 'msgsClientOptions', {
                BASE_URL: '',
                DEFAULT_TIMEOUT: 10000,
                XSRF_COOKIE_NAME: 'CSRF',
                XSRF_HEADER_NAME: 'X-XSRF-TOKEN',
                CLIENT_APP: 'MESSAGES_WEB',
                LOCALE_LANGUAGE: 'en',
                LOCALE_COUNTRY: 'US',
                PAGELOAD_TK: '',
            });

            instanceRegistry.msgsClient = new SimpleMessagingServiceClient(clientOptions);
        }

        // TODO Remove when all usage is moved to AppContext
        if (!instanceRegistry.proctor) {
            instanceRegistry.proctor = new Proctor({
                proctorGroups: _.get(pageConfigData, 'proctor.proctorGroups', {}),
                proctorGroupsString: _.get(pageConfigData, 'proctor.proctorGroupsString', ''),
            });
        }

        // Note: OneGraphClient, RezcowClient, and IMS ApolloClient removed
        // Rearchitecture uses @indeed/messaging-react's MessagingSdk instead

        browserStore = Object.freeze({
            pageConfigData: _.cloneDeep(pageConfigData),
            pageInitData: _.cloneDeep(pageInitData),
            instanceRegistry: _.clone(instanceRegistry),
        });
    }

    return browserStore;
};

// for testing purpose
export const resetBrowserStore = (): void => {
    browserStore = undefined;
};

export const getBrowserStore = (): BrowserStore => {
    if (!browserStore) {
        throw new Error('Browser store has not been initialized yet.');
    }

    return browserStore;
};

// tslint:disable-next-line: no-any
export const getPageConfigDataByKey = (key: string, defaultValue: any): any =>
    _.get(getBrowserStore(), ['pageConfigData', key], defaultValue);

// tslint:disable-next-line: no-any
export const getPageInitDataByKey = (key: string, defaultValue: any): any =>
    _.get(getBrowserStore(), ['pageInitData', key], defaultValue);

// tslint:disable-next-line: no-any
export const getInstanceRegistryByKey = (key: string, defaultValue: any): any =>
    _.get(getBrowserStore(), ['instanceRegistry', key], defaultValue);

export { getStagingEnvironment, isStagingEnvironment, isNativeApp } from './utils/env';
export { getMessagingServiceClient } from './utils/msgsClient';
export { getSessionTrackingKey } from './utils/logging';
export { getDeviceId } from './utils/mobile';
export { getBaseIndeedURL, getResumeURL, getRootURL, getBaseMsgsUrl } from './utils/paths';
export { getMobileAppProctorGrps, getProctor } from './utils/proctor';
export { getLocale } from './utils/locale';
export { getSagaOptions } from './utils/saga';
export { getCurrentUser } from './utils/session';
export { initializeDebugBarData, getDebugBarData } from './utils/debugbar';
export { initializeDebugDataToWindow } from './utils/debugData';

export default {
    initBrowserStore,
    getBrowserStore,
    getPageConfigDataByKey,
    getPageInitDataByKey,
    getInstanceRegistryByKey,
};
